import { useQuery } from '@tanstack/react-query';
import { tenantMetaApi } from '@/api/supports';
import { useTenantContext } from './useTenantContext';

/**
 * Loads the current tenant's enabled modules (from GET /tenants/meta) and
 * exposes a helper to decide whether a module's tenant-admin surfaces should
 * be shown. A SuperAdmin on the platform menu (not impersonating) bypasses
 * gating entirely. When a SuperAdmin impersonates a tenant, we fetch and gate
 * by THAT tenant's modules (apiClient sends X-Impersonate-Tenant), so the menu
 * mirrors what the tenant admin sees. Lookup errors fail closed: an
 * entitlement outage must never expose a disabled tenant surface.
 */
export function useEnabledModules() {
  const { isSuperAdmin, impersonatedTenantId } = useTenantContext();
  const isImpersonating = isSuperAdmin && impersonatedTenantId != null;
  // SuperAdmin only bypasses gating while on the platform menu (no tenant).
  const bypassGating = isSuperAdmin && !isImpersonating;

  const { data, isLoading, isError } = useQuery({
    // Key by the impersonated tenant so switching tenants refetches its modules.
    queryKey: ['tenant-meta-modules', impersonatedTenantId ?? null],
    queryFn: tenantMetaApi.getMeta,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: !bypassGating,
  });

  const enabledModules = data?.enabledModules ?? [];
  const moduleConfigs = data?.moduleConfigs ?? {};

  /**
   * A module is manageable in the tenant portal when it's enabled AND its
   * `tenantPortal` channel isn't explicitly turned off by SuperAdmin
   * (default true). Platform-menu SuperAdmin and lookup errors bypass the gate.
   */
  const isManageable = (moduleKey: string): boolean => {
    if (bypassGating) return true;
    if (isError || !data) return false;
    if (!enabledModules.includes(moduleKey)) return false;
    return moduleConfigs[moduleKey]?.channels?.tenantPortal === true;
  };

  const providerAdminAllowed = bypassGating || (
    !isError && !!data && data.channels?.providerAdminPortal === true
  );

  return {
    enabledModules,
    moduleConfigs,
    isManageable,
    providerAdminAllowed,
    isLoading,
    isError,
    // True once we have a definitive answer (data arrived, errored, or N/A).
    ready: bypassGating || isError || !!data,
  };
}
